import { useSyncExternalStore } from 'react';
import { takeTokenFromUrl, tokenCustomer } from '@mypoz/shared';

/**
 * The customer's login token (MYP-125).
 *
 * It arrives once, in the address (`?token=…`), when the old MyPoz website's button opens this app
 * (the vendor's plan, Q&A v2). It is taken out of the address straight away and kept in the
 * browser, so a reload or a second tab stays signed in for the token's ten days.
 *
 * Kept in `localStorage`, every access guarded: storage can be switched off or throw (a private
 * window). Only then does the token live in memory, for this visit — still signed in until the tab
 * closes, which is the best that browser allows. When storage works it is the one source, so every
 * tab sends the same token (review of c07932a): a copy in memory would keep sending an old token
 * after another tab saved a new one or signed out.
 */
const STORAGE_KEY = 'mypoz_session_token';

let memoryToken: string | null = null;

function readStored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function store(token: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, token);
    memoryToken = null;
  } catch {
    // Storage blocked: `memoryToken` carries this visit.
    memoryToken = token;
  }
}

function forget(): void {
  memoryToken = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing stored to remove.
  }
}

/**
 * The development token from `apps/web/.env.local`, used only when no real one has arrived.
 *
 * Gated on `import.meta.env.DEV`, which Vite replaces with a literal at build time, so a production
 * build drops the branch and the test token never reaches the bundle — even if the variable is set.
 */
const devToken: string | null = import.meta.env.DEV
  ? import.meta.env.VITE_LEGACY_API_TOKEN || null
  : null;

/**
 * Takes `?token=` out of the address and keeps it. Called once in `main.tsx`, before the app
 * renders, so the router never sees the token and it never stays in the history, a bookmark or a
 * copied link. A new token replaces the saved one — another customer opening the link on the same
 * computer.
 */
export function captureTokenFromAddress(): void {
  const { token, cleanUrl } = takeTokenFromUrl(window.location.href);
  if (token) store(token);
  if (cleanUrl !== undefined) window.history.replaceState(window.history.state, '', cleanUrl);
}

/** The token to send with a call, or null — the vendor then answers "invalid token". */
export function sessionToken(): string | null {
  return readStored() ?? memoryToken ?? devToken;
}

/**
 * Follows the sign-in in other tabs (review of c07932a). Called once in `main.tsx`.
 *
 * - The token was removed — the other tab was told it is invalid: this tab shows the signed-out
 *   screen too, rather than failing call by call.
 * - A different customer's token was saved: this tab reloads, so it never shows one customer's
 *   name and balance while sending another's token.
 * - The same customer got a fresh token: nothing to do; the next call reads it from storage.
 *
 * The `storage` event only fires in the other tabs, never in the one that made the change.
 */
export function followOtherTabs(): void {
  window.addEventListener('storage', (event) => {
    // `key` is null when the whole storage was cleared.
    if (event.key !== STORAGE_KEY && event.key !== null) return;
    const now = readStored();
    if (!now) {
      if (event.key === null || event.oldValue) endSession();
      return;
    }
    const before = event.key === null ? null : event.oldValue;
    if (!before || tokenCustomer(before) !== tokenCustomer(now) || tokenCustomer(now) === null) {
      window.location.reload();
    }
  });
}

/* -------------------------------------------------------------------------------------------- *
 * Signed out
 * -------------------------------------------------------------------------------------------- */

let signedOut = false;
const listeners = new Set<() => void>();

/**
 * The vendor said the token is missing, wrong or expired. The saved token is dropped and the app
 * shows its signed-out screen until the customer comes back through the old website. Called by the
 * API clients (`onInvalidToken`); safe to call more than once.
 */
export function endSession(): void {
  forget();
  if (signedOut) return;
  signedOut = true;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Whether the session has ended (see `endSession`). */
export function useSignedOut(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => signedOut,
    () => false,
  );
}
